import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { BlockConfig } from 'components/editor/GridDndEditor/models/BlockConfig.model';
import { GridBlockType } from 'services/entities/BlockTypes';
import { SignatureStatus, SignatureType } from 'services/entities/SignatureTypes';
import type { SignatureBox } from 'services/entities/SignatureTypes';
import type { TableColumnType, TableMetadataType, TableRowType, TableType } from './table.types';

import type { RootState } from './Store';
import type { Section, SectionStyles } from '../../Sections/Section.model';
import type {
  AddSectionType,
  UpdateSectionTitleType,
  BlockDimensionType,
  BlockLayerConfigType,
  BlockPositionType,
  CurrentManipulatedBlockType,
  DeleteBlockStateProps,
  DeleteSectionType,
  InsertImportedSectionType,
  InsertImportedSectionsType,
  MaxHeightPageType,
  TableBlockStateType,
  UpdateBlockContentType,
  UpdateBlockContentObjectType,
  UpdateSectionsOrderType,
} from './editorSlice.types';

import { castDraft, type Draft } from 'immer';

export type BlockContent = {
  content: string;
  contentObject?: Record<string, unknown>;
  contentTable?: TableType;
  contentSignature?: SignatureBox;
  blockConfig: BlockConfig;
  type: GridBlockType;
};

export type BlockMetadata = {
  id: string;
  type: GridBlockType;
  imageAspectRatioLock: boolean;
};

export type BlocksContentCollection = Record<string, BlockContent>;
export type BlocksMetadataCollection = BlockMetadata[];

export type GridState = {
  sections: Array<Section>;
  temporaryDimensions: Record<
    string,
    {
      originalDimensions: { width: number; height: number; x: number; y: number };
      sectionId: string;
    }
  >;
};

const initialState: GridState = {
  sections: [],
  temporaryDimensions: {},
};

export interface BlockAddType {
  sectionId: string;
  blockId: string;
  content: any;
  blockConfig: BlockConfig;
  blockType: GridBlockType;
}

export interface TableActivateHandlerType {
  sectionId: string;
  blockId: string;
  columnId: string;
  activeHoverColumn: boolean;
}

export interface TableColumnSelectedType {
  sectionId: string;
  blockId: string;
  columnId?: string;
  isColumnSelected?: boolean;
}

function buildSectionFromImport(section: InsertImportedSectionType['section']): Section {
  const blocksContent: BlocksContentCollection = {};
  const blocksMetadata: BlockMetadata[] = [];
  const blocksLayer = { greaterZIndexAvailable: 0, lowerZIndexAvailable: 0 };

  section.blocks.forEach((block) => {
    const blockConfig = {
      id: block.blockId,
      x: block.position.left_px,
      y: block.position.top_px,
      width: block.dimensions.width_px,
      height: block.dimensions.height_px,
      z: block.zIndex,
    };

    if (block.type === GridBlockType.TABLE) {
      blocksContent[block.blockId] = { blockConfig, type: block.type, content: '', contentTable: block.tableContent };
    } else if (block.type === GridBlockType.SIGNATURE) {
      const contentSignature: SignatureBox = {
        signatureBoxId: block.blockId,
        type: SignatureType.Signature,
        properties: {
          position: { x: block.position.left_px, y: block.position.top_px },
          dimensions: { width: block.dimensions.width_px, height: block.dimensions.height_px },
        },
        assignedSignee: null,
        status: SignatureStatus.UnSigned,
        signatureEvent: null,
      };
      blocksContent[block.blockId] = { blockConfig, type: block.type, content: '', contentSignature };
    } else {
      blocksContent[block.blockId] = { blockConfig, type: block.type, content: block.htmlContent, contentObject: block.content };
    }

    blocksMetadata.push({ id: block.blockId, type: block.type, imageAspectRatioLock: true });
    blocksLayer.greaterZIndexAvailable = Math.max(block.zIndex + 1, blocksLayer.greaterZIndexAvailable);
    blocksLayer.lowerZIndexAvailable = Math.min(block.zIndex - 1, blocksLayer.lowerZIndexAvailable);
  });

  return {
    id: section.id,
    title: section.title,
    blocksContent,
    blocksMetadata,
    blocksLayer,
    editorConfig: { currentManipulatedBlock: null, maxHeightPage: 0 },
    ...(section.styles ? { styles: section.styles } : {}),
  };
}

const gridBlockSlice = createSlice({
  name: 'editor-grid-block',
  initialState,
  reducers: {
    addGridBlockState(state, action: PayloadAction<BlockAddType>) {
      const { sectionId, blockId, content, blockConfig, blockType } = action.payload;
      const section = state.sections.find((section) => section.id === sectionId);
      if (!section) {
        throw new Error('Section cannot be identified.');
      }

      section.blocksMetadata.push({ id: blockId, type: blockType, imageAspectRatioLock: true });

      const textImageBlockConfig = {
        blockConfig,
        type: blockType,
        content,
      };

      const tableBlockConfig = {
        ...textImageBlockConfig,
        content: '',
        contentTable: content,
      };

      const signatureBlockConfig = {
        ...textImageBlockConfig,
        content: '',
        contentSignature: content,
      };

      if (blockType === 'TABLE') {
        section.blocksContent[blockId] = tableBlockConfig;
      } else if (blockType === 'SIGNATURE') {
        section.blocksContent[blockId] = signatureBlockConfig;
      } else {
        section.blocksContent[blockId] = textImageBlockConfig;
      }
      section.blocksLayer.greaterZIndexAvailable++;
      return state;
    },
    setColumnToActiveHoverColumn(state, action: PayloadAction<TableActivateHandlerType>) {
      const { sectionId, blockId, columnId, activeHoverColumn } = action.payload;
      const section = state.sections.find((section) => section.id === sectionId);
      if (!section) {
        throw new Error('Section cannot be identified.');
      }
      if (!section?.blocksContent?.[blockId]?.contentTable) {
        throw new Error('Block is not a ta ble');
      }

      section.blocksContent?.[blockId]?.contentTable.columns.forEach((column) => {
        if (column.field === columnId) {
          column.activeHoverColumn = activeHoverColumn;
        } else {
          column.activeHoverColumn = false;
        }
        return column;
      });

      return state;
    },
    setColumnToSelected(state, action: PayloadAction<TableColumnSelectedType>) {
      const { sectionId, blockId, columnId, isColumnSelected } = action.payload;
      const section = state.sections.find((section) => section.id === sectionId);

      if (!section) throw new Error('Section cannot be identified.');
      if (!section?.blocksContent?.[blockId]?.contentTable) throw new Error('Block is not a table');

      const columns = section.blocksContent[blockId].contentTable.columns;

      const alreadySelected = columns.find((c) => c.field === columnId);
      if (alreadySelected?.columnSelected) return state;

      columns.forEach((column) => {
        if (isColumnSelected === false) {
          column.columnSelected = false;
        } else {
          column.columnSelected = column.field === columnId;
        }
      });

      return state;
    },
    resizeAdjacentColumns(
      state,
      action: PayloadAction<{
        sectionId: string;
        blockId: string;
        leftColumnId: string;
        rightColumnId: string;
        newLeftWidth: number;
        newRightWidth: number;
      }>
    ) {
      const { sectionId, blockId, leftColumnId, rightColumnId, newLeftWidth, newRightWidth } = action.payload;
      const section = state.sections.find((s) => s.id === sectionId);
      const columns = section?.blocksContent[blockId]?.contentTable?.columns;
      if (!columns) return;

      const leftColumn = columns.find((col) => col.field === leftColumnId);
      const rightColumn = columns.find((col) => col.field === rightColumnId);
      if (!leftColumn || !rightColumn) return;

      const MIN_COLUMN_WIDTH = 52;
      if (newLeftWidth < MIN_COLUMN_WIDTH || newRightWidth < MIN_COLUMN_WIDTH) return;

      leftColumn.width = newLeftWidth;
      rightColumn.width = newRightWidth;
    },
    addTemporaryDimensions(
      state,
      action: PayloadAction<{
        blockId: string;
        sectionId: string;
        originalDimensions: { width: number; height: number; x: number; y: number };
      }>
    ) {
      const { blockId, sectionId, originalDimensions } = action.payload;
      if (!state.temporaryDimensions[blockId]) {
        state.temporaryDimensions[blockId] = { originalDimensions, sectionId };
      }
    },
    clearTemporaryDimensionsForBlock: (state, action: PayloadAction<{ blockId: string }>) => {
      delete state.temporaryDimensions[action.payload.blockId];
    },
    updateGridBlockState(state, action: PayloadAction<UpdateBlockContentType>) {
      const { sectionId, blockId, content } = action.payload;
      const block = state.sections.find((section) => section.id === sectionId)?.blocksContent[blockId];
      if (block) {
        block.content = content;
      }
    },
    updateGridContentObjectBlockState(state, action: PayloadAction<UpdateBlockContentObjectType>) {
      const { sectionId, blockId, content } = action.payload;
      const block = state.sections.find((section) => section.id === sectionId)?.blocksContent[blockId];
      if (block) {
        block.contentObject = content;
      }
    },
    updateGridTableBlockState(state, action: PayloadAction<TableBlockStateType>) {
      const { sectionId, blockId, contentTable } = action.payload;
      const block = state.sections.find((section) => section.id === sectionId)?.blocksContent[blockId];
      if (block) {
        // TODO(SP-3763): selectedRow is client-only UI state that should not live inside contentTable.
        // Preserve it here so optimistic content updates don't clobber a row selection made while a
        // debounce was pending. SP-3763 tracks moving selectedRow to a sibling tableUiState field.
        const currentSelectedRow = block.contentTable?.selectedRow;
        block.contentTable = { ...contentTable, selectedRow: currentSelectedRow };
      }
    },
    updateGridSignatureBlockState(state, action: PayloadAction<{ sectionId: string; blockId: string; contentSignature: SignatureBox }>) {
      const { sectionId, blockId, contentSignature } = action.payload;
      const block = state.sections.find((section) => section.id === sectionId)?.blocksContent[blockId];
      if (block) {
        block.contentSignature = contentSignature;
      }
    },
    setMaxHeightPage(state, action: PayloadAction<MaxHeightPageType>) {
      const { maxHeightPage, sectionId } = action.payload;
      const section = state.sections.find((section) => section.id === sectionId);
      if (section) {
        section.editorConfig.maxHeightPage = maxHeightPage;
      }
    },
    updateGridPositionConfig(state, action: PayloadAction<BlockPositionType>) {
      const { sectionId, blockId, x, y } = action.payload;
      const blockConfig = state.sections.find((section) => section.id === sectionId)?.blocksContent[blockId]?.blockConfig;
      if (blockConfig) {
        blockConfig.x = x;
        blockConfig.y = y;
      }
    },
    updateGridLayerConfig(state, action: PayloadAction<BlockLayerConfigType>) {
      const { sectionId, blockId, zIndex } = action.payload;
      const section = state.sections.find((section) => section.id === sectionId);
      const blockConfig = section?.blocksContent[blockId]?.blockConfig;
      if (blockConfig) {
        blockConfig.z = zIndex;

        const isBlockMovedToFrontLayer = zIndex === section.blocksLayer.greaterZIndexAvailable;
        const isBlockMovedToBackLayer = zIndex === section.blocksLayer.lowerZIndexAvailable;

        if (isBlockMovedToFrontLayer) {
          section.blocksLayer.greaterZIndexAvailable++;
        } else if (isBlockMovedToBackLayer) {
          section.blocksLayer.lowerZIndexAvailable--;
        }
      }
    },
    updateGridDimensionConfig(state, action: PayloadAction<BlockDimensionType>) {
      const { sectionId, blockId, width, height } = action.payload;
      const blockConfig = state.sections.find((section) => section.id === sectionId)?.blocksContent[blockId]?.blockConfig;
      if (blockConfig) {
        blockConfig.width = width;
        blockConfig.height = height;
      }
    },
    updateCurrentBlockForGuideLines(state, action: PayloadAction<CurrentManipulatedBlockType>) {
      const { currentManipulatedBlock, sectionId } = action.payload;
      const section = state.sections.find((section) => section.id === sectionId);
      if (section) {
        section.editorConfig.currentManipulatedBlock = currentManipulatedBlock;
      }
    },
    deleteGridBlockState(state, action: PayloadAction<DeleteBlockStateProps>) {
      const { sectionId, blockId } = action.payload;
      const section = state.sections.find((section) => section.id === sectionId);
      if (!section) return;

      const blockIndex = section.blocksMetadata.findIndex((blockMetadata) => blockMetadata.id === blockId);

      const isBlockInFrontLayer = section.blocksContent[blockId]?.blockConfig.z === section.blocksLayer.greaterZIndexAvailable - 1;
      const isBlockInBackLayer = section.blocksContent[blockId]?.blockConfig.z === section.blocksLayer.lowerZIndexAvailable + 1;

      if (isBlockInFrontLayer) {
        section.blocksLayer.greaterZIndexAvailable = section.blocksContent[blockId].blockConfig.z;
      } else if (isBlockInBackLayer) {
        section.blocksLayer.lowerZIndexAvailable = section.blocksContent[blockId].blockConfig.z;
      }

      section.blocksMetadata.splice(blockIndex, 1);
      delete section.blocksContent[blockId];
    },
    resetSections(state) {
      state.sections = [];
    },
    setInitialState(state, action: PayloadAction<GridState>) {
      const { sections } = action.payload;
      if (sections && typeof sections === 'object') {
        state.sections = [];
        sections.forEach((section) => {
          state.sections.push({
            id: section.id,
            title: section.title,
            blocksContent: (section.blocksContent as Draft<BlocksContentCollection>) || {},
            blocksLayer: section.blocksLayer || { greaterZIndexAvailable: 0, lowerZIndexAvailable: 0 },
            blocksMetadata: section.blocksMetadata || [],
            editorConfig: section.editorConfig || { currentManipulatedBlock: null, maxHeightPage: 0 },
            ...(section.styles ? { styles: section.styles } : {}),
          });
        });
      } else {
        state.sections = [];
      }
    },
    updateSectionTitle(state, action: PayloadAction<UpdateSectionTitleType>) {
      const { sectionId, sectionTitle } = action.payload;
      const section = state.sections.find((section) => section.id === sectionId);
      if (section) {
        section.title = sectionTitle;
      }
    },
    addSection(state, action: PayloadAction<AddSectionType>) {
      const { sectionId, title, afterSectionId } = action.payload;

      const alreadyExists = state.sections.find((section) => section.id === sectionId);
      if (!alreadyExists) {
        const newSection = {
          id: sectionId,
          title: title,
          blocksContent: {},
          blocksLayer: { greaterZIndexAvailable: 0, lowerZIndexAvailable: 0 },
          blocksMetadata: [],
          editorConfig: { currentManipulatedBlock: null, maxHeightPage: 0 },
        };
        if (afterSectionId === null) {
          state.sections.unshift(castDraft(newSection));
        } else {
          const insertIndex = state.sections.findIndex((section) => section.id === afterSectionId) + 1;
          state.sections.splice(insertIndex, 0, castDraft(newSection));
        }
      }
    },
    insertImportedSections(state, action: PayloadAction<InsertImportedSectionsType>) {
      const { sections, afterSectionId } = action.payload;

      if (!Array.isArray(sections)) return;

      let currentAfterSectionId = afterSectionId;

      for (const section of sections) {
        const newSection = buildSectionFromImport(section);

        if (!currentAfterSectionId) {
          state.sections.unshift(castDraft(newSection));
        } else {
          const insertIndex = state.sections.findIndex((s) => s.id === currentAfterSectionId) + 1;
          state.sections.splice(insertIndex, 0, castDraft(newSection));
        }

        currentAfterSectionId = section.id;
      }
    },
    deleteSection(state, action: PayloadAction<DeleteSectionType>) {
      const { sectionId } = action.payload;
      state.sections = state.sections.filter((section) => section.id !== sectionId);
    },
    setSectionStyles(state, action: PayloadAction<{ sectionId: string; styles: SectionStyles }>) {
      const { sectionId, styles } = action.payload;
      const section = state.sections.find((s) => s.id === sectionId);
      if (section) {
        section.styles = styles;
      }
    },
    reorderSections(state, action: PayloadAction<UpdateSectionsOrderType>) {
      const { newSectionsSequence } = action.payload;

      const reorderedSections = state.sections.reduce((acc, section) => {
        const i = newSectionsSequence.findIndex((s) => s.id === section.id);
        acc[i] = section;
        return acc;
      }, new Array(state.sections.length));

      state.sections = reorderedSections;
    },
    moveSectionAfter(state, action: PayloadAction<{ sectionId: string; afterSectionId: string | null }>) {
      const { sectionId, afterSectionId } = action.payload;
      const currentIndex = state.sections.findIndex((s) => s.id === sectionId);
      if (currentIndex === -1) return;

      const [section] = state.sections.splice(currentIndex, 1);

      if (afterSectionId === null) {
        state.sections.unshift(section);
      } else {
        const insertIndex = state.sections.findIndex((s) => s.id === afterSectionId) + 1;
        state.sections.splice(insertIndex, 0, section);
      }
    },
    setSelectedRow(state, action: PayloadAction<{ sectionId: string; blockId: string; row: TableRowType | null }>) {
      const { sectionId, blockId, row } = action.payload;
      const block = state.sections.find((section) => section.id === sectionId)?.blocksContent[blockId];
      if (block?.contentTable) {
        block.contentTable.selectedRow = row;
      }
    },
    updatePricingTotal(state, action: PayloadAction<{ sectionId: string; blockId: string; total: number }>) {
      const { sectionId, blockId, total } = action.payload;
      const block = state.sections.find((section) => section.id === sectionId)?.blocksContent[blockId];

      if (block?.contentTable?.metadata) {
        block.contentTable.metadata.tableTotal = total;
      }
    },
  },
});

export const selectBlockContent = (state: RootState, sectionId: string, blockId: string): BlockContent | undefined => {
  return state.gridBlockReducer.sections.find((section) => section.id === sectionId)?.blocksContent[blockId];
};

export const selectContentTable = (state: RootState, sectionId: string, blockId: string) =>
  selectBlockContent(state, sectionId, blockId)?.contentTable as TableType;

export const selectContentTableRows = (state: RootState, sectionId: string, blockId: string) =>
  selectContentTable(state, sectionId, blockId)?.rows as TableRowType[];

export const selectContentTableColumns = (state: RootState, sectionId: string, blockId: string) =>
  selectContentTable(state, sectionId, blockId)?.columns as TableColumnType[];

export const selectContentTableColumnByField = (state: RootState, sectionId: string, blockId: string, field: string) =>
  selectContentTable(state, sectionId, blockId)?.columns.find((column) => column.field === field) as TableColumnType;

export const selectContentTableMetadata = (state: RootState, sectionId: string, blockId: string) =>
  selectContentTable(state, sectionId, blockId)?.metadata as TableMetadataType;

export const selectTableTotalRows = (state: RootState, sectionId: string, blockId: string) =>
  selectContentTable(state, sectionId, blockId)?.totalRows as TableRowType[];

export const selectTotalSelectedRow = (state: RootState, sectionId: string, blockId: string) =>
  selectContentTable(state, sectionId, blockId)?.selectedRow as TableRowType;

export const selectSectionIdByBlockId = (state: RootState, blockId: string): string | undefined => {
  const sections = state.gridBlockReducer.sections;
  for (const section of sections) {
    if (blockId in section.blocksContent) {
      return section.id;
    }
  }
  return undefined;
};

export const selectSections = (state: RootState): Section[] => {
  return state.gridBlockReducer.sections;
};

export const selectFirstSection = (state: RootState): Section | undefined => state.gridBlockReducer.sections[0];

export const selectFirstSectionId = (state: RootState): string => state.gridBlockReducer.sections[0].id;

export const selectSectionById = (state: RootState, sectionId: string): Section | undefined => {
  return state.gridBlockReducer.sections.find((section) => section.id === sectionId);
};

export const selectSectionStylesById = createSelector([selectSectionById], (section): SectionStyles => section?.styles ?? {});

export const selectSectionTitleById = (state: RootState, sectionId: string) => {
  return state.gridBlockReducer.sections.find((section) => section.id === sectionId)?.title;
};

export const selectDocumentTotal = (state: RootState): number => {
  const sections = state.gridBlockReducer.sections || {};
  let documentTotal = 0;

  for (const sectionId in sections) {
    let sectionTotal = 0;
    Object.values(sections[sectionId]?.blocksContent).forEach((blockContent) => {
      const tableTotal = blockContent.contentTable?.metadata.tableTotal;
      if (tableTotal !== undefined) {
        sectionTotal += tableTotal;
      }
    });
    documentTotal += sectionTotal;
  }
  return documentTotal;
};
export const {
  addGridBlockState,
  updateGridTableBlockState,
  updateGridBlockState,
  updateGridContentObjectBlockState,
  updateGridPositionConfig,
  updateGridLayerConfig,
  updateGridDimensionConfig,
  updateCurrentBlockForGuideLines,
  deleteGridBlockState,
  setMaxHeightPage,
  resetSections,
  setInitialState,
  updateSectionTitle,
  addSection,
  insertImportedSections,
  deleteSection,
  reorderSections,
  setSelectedRow,
  updatePricingTotal,
  updateGridSignatureBlockState,
  addTemporaryDimensions,
  clearTemporaryDimensionsForBlock,
  moveSectionAfter,
  setColumnToActiveHoverColumn,
  resizeAdjacentColumns,
  setColumnToSelected,
  setSectionStyles,
} = gridBlockSlice.actions;

export const gridBlockReducer = gridBlockSlice.reducer;
